import React, { useCallback } from "react";

import {
  KitPermission,
  PrivacyLevel,
  Tooltip,
  Text,
  Kit,
  Button,
  Flex,
  Checkbox,
} from "@thenounproject/lingo-core";

import useKitUpsell, { AccessStatus } from "@hooks/useKitUpsell";
import UpsellTooltip from "../../../components/spaces/settings/UpsellTooltip";
import useUpdateKitPrivacy from "@redux/actions/kits/useUpdateKitPrivacy";

type Props = {
  kit: Kit;
};

const KitSeoCheckbox: React.FC<Props> = ({ kit }) => {
  const seoAccess = useKitUpsell(KitPermission.manageSEO, kit);
  const [editKitPrivacy] = useUpdateKitPrivacy();
  const toggleIndexable = useCallback(
    () =>
      editKitPrivacy({
        kitId: kit.kitId,
        spaceId: kit.spaceId,
        privacy: kit.privacy as PrivacyLevel,
        indexable: !kit.indexable,
      }),
    [editKitPrivacy, kit.indexable, kit.kitId, kit.privacy, kit.spaceId]
  );

  if (seoAccess.accessStatus === AccessStatus.insufficientRole) return null;

  const hasAccess = seoAccess.accessStatus === AccessStatus.canAccess;
  const isSelected = kit.indexable && hasAccess;
  function renderUpsell() {
    return !hasAccess ? (
      <UpsellTooltip
        source="indexable-tooltip"
        onClick={seoAccess.openUpgradeModal}
        featureName="SEO"
      />
    ) : null;
  }

  return (
    <>
      {renderUpsell()}
      <Flex alignItems="center">
        <Flex data-tooltip-trigger="indexable-tooltip">
          <Checkbox
            isSelected={isSelected}
            disabled={!hasAccess}
            label={
              <Flex>
                <Text data-tooltip-source="indexable-tooltip" font="ui.small" color="grayDarkest">
                  Allow search engines to index your kit
                </Text>
              </Flex>
            }
            onClick={toggleIndexable}
          />
        </Flex>
        <Tooltip.InfoTip fill="grayDarkest">
          <Text color="white" font="ui.small">
            When enabled, search engines (Google, Bing, etc.) can discover your kit and display it
            in search results.{" "}
            <Button
              buttonStyle="tertiary"
              tintColor="white"
              text="Learn more."
              fontStyle="ui.smallBold"
              size="small"
              link={"http://help.lingoapp.com/en/articles/7970185-seo"}
              newWindow
            />
          </Text>
        </Tooltip.InfoTip>
      </Flex>
    </>
  );
};

export default KitSeoCheckbox;
